define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/query",
	"dojo/store/Memory",
	"dijit/registry",
	"dgrid/extensions/DijitRegistry",
	"dgrid/OnDemandGrid",
	"dgrid/editor",
	"dgrid/tree",
	"csi",
	"csi/_base/modules",
	"csi/_base/underscore",
	"csi/def",
	"csi/def/Action",
	"csi/def/ViewStoreBuilder",
	"csi/locale",
	"csi/reflection",
	"csi/sessionConstants",
	"csi/state/rootStore",
	"csi/view/ToolbarPluginSelect",
	"csi/widget/CheckBox",
	"csi/xml",
	"./_RoleBasedGrid"
], function(declare, lang, query, Memory, registry, GridDijitRegistry, OnDemandGrid, editor, tree,
	csi, modules, _, def, Action, ViewStoreBuilder, locale, reflection, sessionConstants, {sessionStore}, ToolbarPluginSelect, CheckBox, xml, _RoleBasedGrid) {

	var _filterTypes = {
		BusinessObject: 1,
		CompanyInfo: 1,
		ConfigurableAttributeGroup: 1,
	};

	function _filterClass(rc) {
		return !rc.abstractOrDisabled && !_filterTypes[rc.getType()];
	}

	return declare(_RoleBasedGrid, {
		ATTRIBUTES: {
			editable: "ReadOnlyViews",
			visible: "HiddenViews"
		},

		EDIT_ATTRIBUTES: {
			editable: "canDisable",
			visible: "canHide"
		},

		PORTAL_HOME: {
			"__Host__": "_CS_Data",
			"__Supplier__": "Supplier"
		},

		// String: view id for root views of node type
		_rootViewId: "__Host__",

		// String: type of node being configured
		_typeNode: "_CS_Data",

		_buildView: function() {
			// summary: create and set store based on plugin parameters
			var store = ViewStoreBuilder.getStore(this._typeNode, this._rootViewId, {
				disabled: !sessionConstants.configEditor,
				rolePrefs: sessionStore.getRolePreferences(this._urlRole)
			});

			if (this._grid) {
				this._grid.set("store", store);
			} else {
				this._grid = new (declare([OnDemandGrid, GridDijitRegistry]))({
					"class": "csi-grid-tableView csi-grid-views",
					showFooter: true,
					query: {parent: null},
					store: store,
					columns: [
						tree({
							className: "left",
							field: "name",
							label: locale.resource("View"),
							sortable: false,
							shouldExpand: function(row, level, previouslyExpanded) {
								return previouslyExpanded || !row.data.parent;
							}
						}),
						{
							className: "left",
							field: "target",
							label: locale.resource("TargetClass"),
							sortable: false
						},
						editor({
							autoSave: true,
							canEdit: function(obj) {
								this.editorArgs.disabled = !obj.canHide;
								return true;
							},
							className: "csi-grid-checkbox",
							editor: CheckBox,
							editorArgs: {},
							field: "visible",
							label: locale.resource("Visible"),
							sortable: false
						}),
						editor({
							autoSave: true,
							canEdit: function(obj) {
								this.editorArgs.disabled = !obj.canDisable || !obj.visible;
								return true;
							},
							className: "csi-grid-checkbox",
							editor: CheckBox,
							editorArgs: {},
							field: "editable",
							label: locale.resource("Editable"),
							sortable: false
						})
					]
				}, this.contentNode);

				this._grid.on("dgrid-datachange", lang.hitch(this, this._onDataChange));
			}

			this.inherited(arguments);
		},

		_getChangedChildren: function(/*Object[]*/changes, /*Object*/item, /*String*/attr, /*Boolean*/b) {
			// summary: iterate through children to determine which ones need to change
			var editAttr = this.EDIT_ATTRIBUTES[attr];

			_.each(item.children, function(child) {
				if (child && child[editAttr] && (child[attr] != b)) {
					this._getChangedChildren(changes, child, attr, b);
					changes.push(child);
				}
			}, this);
		},

		_getToolbarActions: function() {
			// summary: return list of toolbar actions
			var portals = [{id: "__Host__", name: locale.resource("ViewConfigurationHost")}];

			if (modules.hasModule("Sourcing")) {
				portals.push({id: "__Supplier__", name: locale.resource("SupplierTypeSupplier")});
			}

			return [
				lang.mixin(new Action(), {
					m_id: "ViewConfigSelectPortal",
					m_onAction: "_onPortal",
					classToolbarPlugin: ToolbarPluginSelect,
					classToolbarPluginParams: {
						showLabel: true,
						store: new Memory({data: portals}),
						value: this._rootViewId
					}
				}),
				this._getRoleAction(),
				lang.mixin(new Action(), {
					m_id: "ViewConfigSelectType",
					m_onAction: "_onType",
					classToolbarPlugin: ToolbarPluginSelect,
					classToolbarPluginParams: {
						showLabel: true,
						store: def.getPortalTypeStore(this._rootViewId, _filterClass),
						value: this._typeNode,
						query: {
							id: {
								test: lang.hitch(this, this._isAllowedTypeItem)
							}
						}
					}
				}),
				"refresh"
			];
		},

		_onDataChange: function(evt) {
			// summary: if flag changed on internal node, then propagate to children;
			// if leaf flag changed to true, then mark parents as true;
			// if leaf flag changed to false, then mark parent to false if all children are false
			var b = evt.value;
			var attr = evt.cell.column.field;
			var item = evt.cell.row.data;

			// protect against recursion
			if (item[attr] == b) {
				return;
			}

			// autoSave kicks on only after this is called, so cannot rely on item having new value
			// for call to fEqual unless we do it ourselves
			var changes = [item];
			item[attr] = b;

			// collect children that need to change
			this._getChangedChildren(changes, item, attr, b);

			// propagate change to parent(s)
			var grid = this._grid;
			var store = grid.store;
			var last = item;
			var fEqual = b ?
				function() {return item[attr];} :
				function() {return _.some(item.children, function(child) {return (child != last) && child[attr];});};

			while (item = store.get(item.parent)) {
				if (fEqual()) break;
				changes.push(item);
				last = item;
			}

			// apply changes
			var attrId = this.ATTRIBUTES[attr];
			var bVisible = (attrId == this.ATTRIBUTES.visible);

			changes = _.map(changes, function(item) {
				// update store
				item[attr] = b;
				store.put(item);

				// could not get Observable store to work, so have to update checkboxes manually
				var row = grid.row(item.id);
				if (row.element) {
					var cbs = query(".dijitCheckBox", row.element);
					var vcb = registry.byNode(cbs[0]);
					var ecb = registry.byNode(cbs[1]);

					if (bVisible) {
						// update value of visible cb and disabled status of editable cb
						vcb.set("value", b, false);
						ecb.set("disabled", !b || !item.canDisable);
					} else {
						// update value of editable cb
						ecb.set("value", b, false);
					}
				}

				return item.id;
			});

			// publish change to server
			var url = this._getPreferencesUrl();

			this.writeOperations((writer) => {
				writer.changeNode(url).changeAttribute(attrId, xml.ATTR_STRINGLIST, changes, !b, b);
			});
		},

		_onPortal: function(/*Controller*/ac) {
			// summary: switch between host and supplier portal which will affect list of available types
			var typePlugin = this.getPlugin("ViewConfigSelectType");
			this._rootViewId = ac.actionValue;
			typePlugin.set("store", def.getPortalTypeStore(this._rootViewId, _filterClass));
			typePlugin.set("value", this.PORTAL_HOME[this._rootViewId]);
		},

		_onType: function(/*Controller*/ac) {
			this._typeNode = ac.actionValue;
			this.refreshWidget(true);
		},

		_isAllowedTypeItem: function(value, item) {
			return (this._urlRole != csi.EMPTY_URL) || (value != reflection.TYPE_SITE);
		},

		_onRole: function(/*Controller*/ac) {
			// summary: switch between host and supplier portal which will affect list of available types
			this._urlRole = ac.actionValue;
			if (this._urlRole == csi.EMPTY_URL && this._typeNode == reflection.TYPE_SITE) {
				var typePlugin = this.getPlugin("ViewConfigSelectType");
				typePlugin.set("value", this.PORTAL_HOME[this._rootViewId]);
			} else {
				this._refreshWidget();
			}
		}
	});
});
